import { isIndicadorRole, isPjRole } from '@/config/routeAccess'
import api from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import type {
  Bloco,
  BlocoAttachment,
  BlocoBadgeResponse,
  BlocoDetail,
  BlocoDetailResponse,
  BlocoKanbanColumn,
  BlocoKanbanColumnResponse,
  BlocoKanbanColumnsResponse,
  BlocoLabel,
  BlocoLabelResponse,
  BlocoLabelsResponse,
  BlocoMessage,
  BlocoMessageResponse,
  BlocoResponse,
  BlocosListResponse,
  CreateBlocoKanbanColumnPayload,
  CreateBlocoLabelPayload,
  CreateBlocoPayload,
  PostBlocoMessagePayload,
  ReorderBlocoKanbanColumnsPayload,
  UpdateBlocoKanbanColumnPayload,
  UpdateBlocoLabelPayload,
  UpdateBlocoMembersPayload,
  UpdateBlocoPayload,
} from '@/types/bloco'

export async function fetchBlocos(): Promise<Bloco[]> {
  const { data } = await api.get<BlocosListResponse>('/blocos')
  return data.data ?? []
}

export async function fetchBlocosBadgeCount(): Promise<number> {
  const role = useAuthStore.getState().user?.role ?? ''
  if (isIndicadorRole(role) || isPjRole(role)) return 0
  const { data } = await api.get<BlocoBadgeResponse>('/blocos/badge-count')
  return data.data?.count ?? 0
}

export async function fetchBlocoDetail(id: string): Promise<BlocoDetail> {
  const { data } = await api.get<BlocoDetailResponse>(`/blocos/${id}`)
  return data.data
}

export async function createBloco(payload: CreateBlocoPayload): Promise<Bloco> {
  const { data } = await api.post<BlocoResponse>('/blocos', payload)
  return data.data
}

export async function updateBloco(id: string, payload: UpdateBlocoPayload): Promise<Bloco> {
  const { data } = await api.patch<BlocoResponse>(`/blocos/${id}`, payload)
  return data.data
}

export async function updateBlocoMembers(
  id: string,
  payload: UpdateBlocoMembersPayload,
): Promise<Bloco> {
  const { data } = await api.patch<BlocoResponse>(`/blocos/${id}/members`, payload)
  return data.data
}

export async function postBlocoMessage(
  id: string,
  payload: PostBlocoMessagePayload,
): Promise<BlocoMessage> {
  const { data } = await api.post<BlocoMessageResponse>(`/blocos/${id}/messages`, payload)
  return data.data
}

export async function uploadBlocoAttachment(blocoId: string, file: File): Promise<BlocoAttachment> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post<{ success: boolean; data: BlocoAttachment }>(
    `/blocos/${blocoId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data.data
}

export async function deleteBloco(id: string): Promise<void> {
  await api.delete(`/blocos/${id}`)
}

export async function fetchBlocoLabels(): Promise<BlocoLabel[]> {
  const { data } = await api.get<BlocoLabelsResponse>('/blocos/labels')
  return data.data ?? []
}

export async function createBlocoLabel(payload: CreateBlocoLabelPayload): Promise<BlocoLabel> {
  const { data } = await api.post<BlocoLabelResponse>('/blocos/labels', payload)
  return data.data
}

export async function updateBlocoLabel(id: string, payload: UpdateBlocoLabelPayload): Promise<BlocoLabel> {
  const { data } = await api.patch<BlocoLabelResponse>(`/blocos/labels/${id}`, payload)
  return data.data
}

export async function deleteBlocoLabel(id: string): Promise<void> {
  await api.delete(`/blocos/labels/${id}`)
}

export async function fetchBlocoKanbanColumns(): Promise<BlocoKanbanColumn[]> {
  const { data } = await api.get<BlocoKanbanColumnsResponse>('/blocos/kanban-columns')
  return data.data ?? []
}

export async function createBlocoKanbanColumn(
  payload: CreateBlocoKanbanColumnPayload,
): Promise<BlocoKanbanColumn> {
  const { data } = await api.post<BlocoKanbanColumnResponse>('/blocos/kanban-columns', payload)
  return data.data
}

export async function updateBlocoKanbanColumn(
  id: string,
  payload: UpdateBlocoKanbanColumnPayload,
): Promise<BlocoKanbanColumn> {
  const { data } = await api.patch<BlocoKanbanColumnResponse>(`/blocos/kanban-columns/${id}`, payload)
  return data.data
}

export async function reorderBlocoKanbanColumns(
  payload: ReorderBlocoKanbanColumnsPayload,
): Promise<BlocoKanbanColumn[]> {
  const { data } = await api.patch<BlocoKanbanColumnsResponse>('/blocos/kanban-columns/reorder', payload)
  return data.data ?? []
}

export async function deleteBlocoKanbanColumn(id: string): Promise<void> {
  await api.delete(`/blocos/kanban-columns/${id}`)
}

export async function moveBlocoKanban(
  id: string,
  kanbanColumnId: string,
  sortOrder?: number,
): Promise<Bloco> {
  return updateBloco(id, { kanbanColumnId, ...(sortOrder !== undefined ? { sortOrder } : {}) })
}
